import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { parseYouTubeId, youTubeEmbedUrl, youTubeThumbnailUrl } from '../utils/youtube';

import { PlayIcon } from './icons';

interface YouTubeVideoProps {
  /** The stored YouTube id. */
  videoId: string;
  title: string | null;
  /** Sizing for the frame, e.g. `aspect-video w-full`; the player fills it. */
  className: string;
  /** Play badge position and size: centred and large in the gallery, small in a card. */
  badge?: 'center' | 'corner';
}

/**
 * The thumbnail stands in for the player until it is tapped: an embedded
 * player per video costs a large script download each, and a stand gallery can
 * hold many. Tapping swaps in the player, which then starts on its own.
 */
export function YouTubeVideo({
  videoId,
  title,
  className,
  badge = 'center',
}: YouTubeVideoProps): ReactElement {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const id = parseYouTubeId(videoId);

  if (id === undefined) {
    return <div className={`bg-ink-800 ${className}`} />;
  }

  if (playing) {
    return (
      <iframe
        src={youTubeEmbedUrl(id)}
        title={title ?? t('stand.playVideo')}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        className={`border-0 bg-black ${className}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setPlaying(true);
      }}
      aria-label={title === null ? t('stand.playVideo') : `${t('stand.playVideo')}: ${title}`}
      className={`relative block overflow-hidden bg-ink-800 ${className}`}
    >
      {/* `object-cover` crops the letterbox bars YouTube bakes into the 4:3 thumbnail. */}
      <img src={youTubeThumbnailUrl(id)} alt="" loading="lazy" className="size-full object-cover" />
      <span
        className={`absolute inset-0 flex ${
          badge === 'center' ? 'items-center justify-center' : 'items-end justify-end p-3'
        }`}
      >
        <span
          className={`flex items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm ${
            badge === 'center' ? 'size-16' : 'size-11'
          }`}
        >
          <PlayIcon
            className={badge === 'center' ? 'size-7 translate-x-0.5' : 'size-5 translate-x-px'}
          />
        </span>
      </span>
    </button>
  );
}
